import { FileText, ImageIcon, Search } from 'lucide-react';
import { CBLogo } from '../CBLogo';
import { cn } from '../../lib/utils';
import IntentActionCard from './IntentActionCard';
import MarkdownView from '../MarkdownView';
import SpeakButton from '../SpeakButton';
import { providerMarkFor } from './providerMarks';
import { RefineImageControl } from './RefineImageControl';
import { SaveImageButton } from './SaveImageButton';

/**
 * Renders a single chat message in the AI Hub. Handles three rendering paths:
 * 1. Single action-plan card (intent router result)
 * 2. Multi-step chain of N action cards
 * 3. Normal markdown bubble (with optional image / PDF / spreadsheet
 *    attachments, brain tool-trace pills, and speak button)
 *
 * Extracted from pages/AIHub.jsx — UI-only, all side effects come in via
 * callback props.
 */
export function MessageBubble({
  message,
  workspaceId,
  onIntentResolved,
  onIntentCancel,
  sending,
  onRefineImage,
  onSaveImage,
  isLatest,
  onQuickReply,
}) {
  const isUser = message.role === 'user';
  const ProviderMark = !isUser ? providerMarkFor(message.model) : null;

  // Pending-categorization quick actions: when the AI just PROPOSED (but did
  // not apply) transaction categorizations, surface Approve / Reject buttons
  // so the user can confirm with one click instead of typing.
  const trace = message.tool_calls_trace || [];
  const hasProposal = trace.some(
    (t) => t.tool === 'propose_transaction_categories' && t.ok && !/proposed 0 /.test(t.summary || ''),
  );
  const hasApply = trace.some((t) => t.tool === 'apply_transaction_categories');
  const showCategorizationActions =
    !isUser && isLatest && !sending && onQuickReply && hasProposal && !hasApply;

  // Action card path — bypasses the normal markdown bubble.
  if (!isUser && message.action_plan) {
    return (
      <div className="flex items-start gap-3" data-testid="ai-hub-message-action">
        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-white/5">
          <CBLogo size="sm" />
        </div>
        <div className="flex-1 max-w-[85%]">
          <IntentActionCard
            plan={message.action_plan}
            workspaceId={workspaceId}
            onResolved={onIntentResolved}
            onCancel={onIntentCancel}
          />
        </div>
      </div>
    );
  }

  // Multi-step chain: render N stacked action cards in a single bubble with
  // a small "Step 1 of N" indicator above each. Users confirm each
  // independently — no auto-cascade since each card may write to the ledger.
  if (!isUser && Array.isArray(message.action_plans) && message.action_plans.length > 0) {
    return (
      <div className="flex items-start gap-3" data-testid="ai-hub-message-chain">
        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-white/5">
          <CBLogo size="sm" />
        </div>
        <div className="flex-1 max-w-[85%] space-y-2.5">
          <div className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-2" data-testid="chain-summary">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" />
            <span>
              {message.chain_summary || `${message.action_plans.length} actions detected`} — review and confirm each below
            </span>
          </div>
          {message.action_plans.map((subPlan, i) => (
            <div key={i} className="relative" data-testid={`chain-step-${i}`}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 dark:text-zinc-500 font-medium mb-1">
                Step {i + 1} of {message.action_plans.length}
              </div>
              <IntentActionCard
                plan={subPlan}
                workspaceId={workspaceId}
                onResolved={onIntentResolved}
                onCancel={onIntentCancel}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`ai-hub-message-${message.role}`}>
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-zinc-200 dark:bg-zinc-700' : 'bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-white/5'
        }`}
      >
        {isUser ? (
          <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">You</span>
        ) : ProviderMark ? (
          <ProviderMark className="w-4 h-4" />
        ) : (
          <CBLogo size="sm" />
        )}
      </div>
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        {/* Brain tool trace (assistant only): collapsed "🔍 Looked up …" pills
          * so the user can see what the AI actually consulted before answering. */}
        {!isUser && message.tool_calls_trace?.length > 0 && (
          <div className="mb-1.5 flex flex-wrap gap-1" data-testid="ai-hub-tool-trace">
            {message.tool_calls_trace.map((t, i) => (
              <span
                key={i}
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono',
                  t.ok ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
                )}
                title={`Tool: ${t.tool}\nArgs: ${JSON.stringify(t.args)}`}
              >
                <Search className="w-2.5 h-2.5" />
                {t.summary}
              </span>
            ))}
          </div>
        )}
        <div
          className={`inline-block px-4 py-3 rounded-2xl text-sm leading-relaxed text-left ${
            isUser
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap'
              : 'bg-white dark:bg-zinc-900/60 border border-zinc-200 dark:border-white/5 text-zinc-800 dark:text-zinc-200 shadow-sm'
          }`}
        >
          {/* Assistant messages render proper markdown (headings, bold, lists,
            * tables, code). User messages stay plain so newlines preserve and
            * we don't accidentally interpret pasted text as markdown. */}
          {isUser ? message.content : <MarkdownView>{message.content}</MarkdownView>}
        </div>
        {!isUser && message.content && (
          <SpeakButton text={message.content} messageId={message.message_id} />
        )}
        {showCategorizationActions && (
          <div className="mt-2 flex items-center gap-2" data-testid="categorization-quick-actions">
            <button
              type="button"
              onClick={() => onQuickReply('Yes — post all the proposed categorizations to my books.')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
              data-testid="approve-categorizations-btn"
            >
              ✓ Yes, post them
            </button>
            <button
              type="button"
              onClick={() => onQuickReply("No — don't post the proposed categorizations.")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-zinc-300 dark:border-white/10 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-white/5 text-xs font-medium transition-colors"
              data-testid="reject-categorizations-btn"
            >
              ✗ No, don't post
            </button>
          </div>
        )}
        {message.attachments?.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {message.attachments.map((a, i) => {
              if (a.kind === 'pdf_download' && a.url) {
                return (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={a.filename}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 text-xs font-medium text-emerald-800 dark:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-emerald-950/50 transition-colors"
                    data-testid={`ai-hub-pdf-download-${i}`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>{a.filename || 'document.pdf'}</span>
                    <span className="text-emerald-600 dark:text-emerald-400 text-[10px] font-normal">
                      Click to download
                    </span>
                  </a>
                );
              }
              if (a.kind === 'image_generated' && a.url) {
                return (
                  <div
                    key={i}
                    className="space-y-1.5"
                    data-testid={`ai-hub-image-generated-${i}`}
                  >
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block max-w-[420px] rounded-lg overflow-hidden border border-zinc-200 dark:border-white/10 hover:opacity-95 transition-opacity"
                    >
                      <img
                        src={a.url}
                        alt={a.prompt || a.filename || 'AI-generated image'}
                        className="w-full h-auto block"
                        loading="lazy"
                      />
                    </a>
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href={a.url}
                        download={a.filename}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800/50 text-xs font-medium text-violet-800 dark:text-violet-200 hover:bg-violet-100 dark:hover:bg-violet-950/50 transition-colors"
                      >
                        <ImageIcon className="w-4 h-4" />
                        <span>{a.filename || 'image.png'}</span>
                        <span className="text-violet-600 dark:text-violet-400 text-[10px] font-normal">
                          {a.edited_from_reference ? 'Edited via Nano Banana' : 'Generated by Nano Banana'}
                        </span>
                      </a>
                      {onRefineImage && (
                        <RefineImageControl
                          imageUrl={a.url}
                          imageFilename={a.filename}
                          onRefine={onRefineImage}
                          disabled={sending}
                          attachmentIndex={i}
                        />
                      )}
                      {onSaveImage && (
                        <SaveImageButton
                          imageUrl={a.url}
                          imageFilename={a.filename}
                          imagePrompt={a.prompt}
                          onSave={onSaveImage}
                          attachmentIndex={i}
                        />
                      )}
                    </div>
                  </div>
                );
              }
              if (a.kind === 'spreadsheet_download' && a.url) {
                const isCsv = a.format === 'csv' || (a.filename || '').toLowerCase().endsWith('.csv');
                return (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={a.filename}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800/50 text-xs font-medium text-sky-800 dark:text-sky-200 hover:bg-sky-100 dark:hover:bg-sky-950/50 transition-colors"
                    data-testid={`ai-hub-spreadsheet-download-${i}`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>{a.filename || (isCsv ? 'data.csv' : 'data.xlsx')}</span>
                    <span className="text-sky-600 dark:text-sky-400 text-[10px] font-normal">
                      Click to download
                    </span>
                  </a>
                );
              }
              return (
                <div key={i} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500">
                  {a.kind === 'pdf_text' ? <FileText className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                  <span>{a.filename}</span>
                </div>
              );
            })}
          </div>
        )}
        {message.model && !isUser && (
          <div className="mt-1 text-[10px] text-zinc-400 dark:text-zinc-600">{message.model}</div>
        )}
      </div>
    </div>
  );
}

export default MessageBubble;
